import React, { useState, useEffect } from 'react';

/**
 * Renderiza el texto de preview de la plantilla:
 * - {{n}} → resaltado en amarillo/verde según si ya tiene valor
 * - *texto* → negrita sin mostrar los asteriscos
 */
const HighlightedPreview = ({ text, values }) => {
    if (!text) return null;
    // Dividir por variables {{n}} y por negritas *...*
    const parts = text.split(/(\{\{\d+\}\}|\*[^*]+\*)/g);
    return (
        <span style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {parts.map((part, i) => {
                const varMatch = part.match(/\{\{(\d+)\}\}/);
                if (varMatch) {
                    const idx = parseInt(varMatch[1], 10) - 1;
                    const val = values[idx];
                    return (
                        <strong key={i} style={{
                            background: val ? '#e8f5e9' : '#fff9c4',
                            padding: '0 3px',
                            borderRadius: 3,
                            color: val ? '#2e7d32' : '#f57f17',
                            fontWeight: 700
                        }}>
                            {val || part}
                        </strong>
                    );
                }
                const boldMatch = part.match(/^\*([^*]+)\*$/);
                if (boldMatch) {
                    return <strong key={i}>{boldMatch[1]}</strong>;
                }
                return <span key={i}>{part}</span>;
            })}
        </span>
    );
};

/**
 * Modal para seleccionar y enviar una plantilla aprobada de WhatsApp.
 *
 * @param {Array}    plantillas  - Array de plantillas cargadas desde el backend
 * @param {Function} onClose     - Callback para cerrar el modal
 * @param {Function} onSend      - async (nombrePlantilla: string, variables: string[]) => void
 */
const TemplateModal = ({ plantillas = [], onClose, onSend }) => {
    const [selectedIdx, setSelectedIdx] = useState('');
    const [varValues, setVarValues] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    const plantilla = selectedIdx !== '' ? plantillas[parseInt(selectedIdx, 10)] : null;

    /**
     * Lista efectiva de variables:
     * - Si el backend ya la provee con etiquetas, la usamos tal cual.
     * - Si viene vacía pero el preview contiene {{n}}, generamos entradas automáticas.
     */
    const effectiveVars = (() => {
        if (!plantilla) return [];
        // Normalizar: la API .NET puede envolver arrays en { $values: [...] }
        const vars = plantilla.variables?.$values || plantilla.variables;
        if (vars?.length > 0) return vars;
        // Auto-detectar {{1}}, {{2}}, ... en el texto de la plantilla
        const matches = [...new Set(
            (plantilla.textoPreview || '').match(/\{\{(\d+)\}\}/g) || []
        )].sort();
        return matches.map(m => ({
            placeholder: m,
            etiqueta: `Variable ${m.replace(/[{}]/g, '')}`,
            sugerencia: ''
        }));
    })();

    // Resetear campos de variables cuando cambia la selección
    useEffect(() => {
        setVarValues(effectiveVars.map(v => v.valorDefecto || ''));
        setError('');
    }, [selectedIdx]);

    const allVarsFilled =
        effectiveVars.length === 0 ||
        varValues.every(v => v.trim() !== '');

    const handleSend = async () => {
        if (!plantilla || !allVarsFilled || sending) return;
        setSending(true);
        setError('');
        try {
            await onSend(plantilla.nombre, varValues);
            onClose();
        } catch (err) {
            setError(err.message || 'No se pudo enviar la plantilla');
        } finally {
            setSending(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="template-modal-overlay" onClick={handleOverlayClick}>
            <div className="template-modal">
                {/* Header */}
                <div className="template-modal-header">
                    <h3>
                        <i className="fa fa-list-alt" style={{ marginRight: 8 }}></i>
                        Enviar Plantilla
                    </h3>
                    <button className="template-modal-close" onClick={onClose} title="Cerrar">
                        <i className="fa fa-times"></i>
                    </button>
                </div>

                {/* Body */}
                <div className="template-modal-body">
                    {/* Selector */}
                    <div className="template-field-group">
                        <label className="template-field-label">Plantilla</label>
                        <select
                            className="template-select"
                            value={selectedIdx}
                            onChange={e => setSelectedIdx(e.target.value)}
                        >
                            <option value="">— Elige una plantilla —</option>
                            {plantillas.map((p, i) => (
                                <option key={p.nombre} value={i}>{p.descripcion}</option>
                            ))}
                        </select>
                    </div>

                    {plantilla && (
                        <>
                            {/* Preview */}
                            <div className="template-field-group">
                                <label className="template-field-label">Vista previa</label>
                                <div className="template-preview-box">
                                    <HighlightedPreview text={plantilla.textoPreview} values={varValues} />
                                </div>
                            </div>

                            {/* Variables */}
                            {effectiveVars.length > 0 && (
                                <div className="template-field-group">
                                    <label className="template-field-label">Variables</label>
                                    {effectiveVars.map((v, i) => (
                                        <div key={i} className="template-var-row">
                                            <span className="template-var-label">{v.etiqueta}</span>
                                            <input
                                                type="text"
                                                className="template-var-input"
                                                placeholder={v.sugerencia || `Valor para ${v.placeholder}`}
                                                value={varValues[i] || ''}
                                                onChange={e => {
                                                    const next = [...varValues];
                                                    next[i] = e.target.value;
                                                    setVarValues(next);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="template-error">
                            <i className="fa fa-exclamation-circle" style={{ marginRight: 6 }}></i>
                            {error}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="template-modal-footer">
                    <button
                        className="template-btn-cancel"
                        onClick={onClose}
                        disabled={sending}
                    >
                        Cancelar
                    </button>
                    <button
                        className="template-btn-send"
                        onClick={handleSend}
                        disabled={!plantilla || !allVarsFilled || sending}
                    >
                        {sending ? (
                            <>
                                <i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }}></i>
                                Enviando...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-paper-plane" style={{ marginRight: 6 }}></i>
                                Enviar Plantilla
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TemplateModal;
